/**
 * Onboarding Service
 * Manages client onboarding with secure token-based links
 */

import { BaseService } from './BaseService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class OnboardingService extends BaseService {
    constructor() {
        super('/api/onboarding', ['createdAt', 'expiresAt', 'submittedAt', 'lastModifiedAt']);
    }

    /**
     * Create onboarding link and send email (Protected - requires JWT)
     * @param {string} accessToken - JWT access token
     * @param {CreateOnboardingRequest} request - Onboarding request data
     * @returns {Promise<OnboardingTokenResponse>} Created token details
     */
    async createOnboardingLink(accessToken, request) {
        this.validateRequiredFields(request, ['clientEmail']);
        
        try {
            const response = await this.makeRequest(accessToken, '/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request)
            });
            return response;
        } catch (error) {
            console.error('Failed to create onboarding link:', error);
            throw error;
        }
    }

    /**
     * Get onboarding form data (Public - no auth required)
     * @param {string} token - Onboarding token
     * @returns {Promise<OnboardingFormData>} Form data and validation status
     */
    async getOnboardingForm(token) {
        try {
            const url = `${API_BASE_URL}/public/v1/onboarding/${token}`;
            
            const response = await fetch(url, {
                method: 'GET'
            });

            // For public endpoints, return the JSON regardless of status
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to get onboarding form:', error);
            return {
                valid: false,
                error: 'network_error',
                message: 'Failed to load form'
            };
        }
    }

    /**
     * Submit onboarding data (Public - no auth required)
     * @param {string} token - Onboarding token
     * @param {SubmitOnboardingRequest} data - Form submission data
     * @returns {Promise<SubmitOnboardingResponse>} Submission result
     */
    async submitOnboarding(token, data) {
        try {
            const url = `${API_BASE_URL}/public/v1/onboarding/${token}/submit`;
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            // Return response as-is (includes success/error)
            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Failed to submit onboarding:', error);
            return {
                success: false,
                error: 'network_error',
                message: 'Failed to submit registration'
            };
        }
    }

    /**
     * Get submission data for a token (Protected - requires JWT)
     * Retrieves decrypted submission data that client submitted through the form.
     * Access is automatically logged in audit trail for security compliance.
     * @param {string} accessToken - JWT access token
     * @param {string} token - Onboarding token ID
     * @returns {Promise<SubmissionDataResponse>} Submission data or error
     */
    async getSubmissionData(accessToken, token) {
        try {
            const response = await this.makeRequest(accessToken, `/submission/${token}`);
            return response;
        } catch (error) {
            console.error('Failed to get submission data:', error);
            
            // Handle 404 specifically
            if (error.message && error.message.includes('404')) {
                return {
                    success: false,
                    error: 'not_found',
                    message: 'No submission data found for this token'
                };
            }
            
            throw error;
        }
    }

    /**
     * Invalidate all onboarding tokens for an email (Protected - requires JWT)
     * @param {string} accessToken - JWT access token
     * @param {InvalidateTokensRequest} request - Email to invalidate tokens for
     * @returns {Promise<InvalidateTokensResponse>} Invalidation result
     */
    async invalidateTokens(accessToken, request) {
        this.validateRequiredFields(request, ['email']);

        try {
            const response = await this.makeRequest(accessToken, '/invalidate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request)
            });
            return response;
        } catch (error) {
            console.error('Failed to invalidate tokens:', error);
            throw error;
        }
    }

    /**
     * Get all onboarding requests (Protected - requires JWT)
     * @param {string} accessToken - JWT access token
     * @param {Object} [filters] - Optional filters
     * @param {boolean} [filters.includeUsed] - Include used tokens (default: true)
     * @param {boolean} [filters.includeExpired] - Include expired tokens (default: true)
     * @param {string} [filters.email] - Filter by email
     * @returns {Promise<OnboardingRequest[]>} List of onboarding requests
     */
    async getOnboardingRequests(accessToken, filters = {}) {
        try {
            const queryParams = new URLSearchParams();
            if (filters.includeUsed !== undefined) queryParams.append('includeUsed', filters.includeUsed);
            if (filters.includeExpired !== undefined) queryParams.append('includeExpired', filters.includeExpired);
            if (filters.email) queryParams.append('email', filters.email);

            const queryString = queryParams.toString();
            const endpoint = queryString ? `/requests?${queryString}` : '/requests';
            
            const response = await this.makeRequest(accessToken, endpoint);
            return response;
        } catch (error) {
            console.error('Failed to get onboarding requests:', error);
            
            // Provide more helpful error context
            if (error.message && error.message.includes('500')) {
                const enhancedError = new Error(
                    'The onboarding requests endpoint is not available. ' +
                    'This feature may not be implemented on the backend yet. ' +
                    'Please contact your system administrator.'
                );
                enhancedError.originalError = error;
                enhancedError.isNotImplemented = true;
                throw enhancedError;
            }
            
            throw error;
        }
    }

    /**
     * Get details for a specific onboarding request (Protected - requires JWT)
     * @param {string} accessToken - JWT access token
     * @param {string} tokenId - Token ID to get details for
     * @returns {Promise<OnboardingRequestDetails>} Onboarding request details
     */
    async getOnboardingRequestDetails(accessToken, tokenId) {
        try {
            const response = await this.makeRequest(accessToken, `/requests/${tokenId}`);
            return response;
        } catch (error) {
            console.error('Failed to get onboarding request details:', error);
            
            // Provide more helpful error context
            if (error.message && error.message.includes('500')) {
                const enhancedError = new Error(
                    'Unable to retrieve request details. ' +
                    'This feature may not be fully implemented on the backend yet.'
                );
                enhancedError.originalError = error;
                enhancedError.isNotImplemented = true;
                throw enhancedError;
            }
            
            throw error;
        }
    }

    /**
     * Cancel/expire a specific onboarding token (Protected - requires JWT)
     * @param {string} accessToken - JWT access token
     * @param {string} tokenId - Token ID to cancel
     * @returns {Promise<Object>} Cancellation result
     */
    async cancelToken(accessToken, tokenId) {
        try {
            const response = await this.makeRequest(accessToken, `/requests/${tokenId}/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            return response;
        } catch (error) {
            console.error('Failed to cancel token:', error);
            throw error;
        }
    }
}

// Export singleton instance
const onboardingService = new OnboardingService();
export default onboardingService;
